/**
 * Error classes for the ZapSEA JavaScript SDK
 */

export class ZapSEAError extends Error {
  public readonly status?: number;
  public readonly code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'ZapSEAError';
    this.status = status ?? undefined;
    this.code = code ?? undefined;
    
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ZapSEAError);
    }
  }

  static async fromResponse(response: Response): Promise<ZapSEAError> {
    let message = `HTTP ${response.status}`;
    let code: string | undefined;

    try {
      const errorData = await response.json();
      message = errorData.detail || errorData.message || message;
      code = errorData.code;
    } catch {
      message = `HTTP ${response.status}: ${response.statusText}`;
    }

    // Map status codes to specific error types
    switch (response.status) {
      case 401:
        return new AuthenticationError(message, response.status, code);
      case 403:
        return new PermissionError(message, response.status, code);
      case 404:
        return new NotFoundError(message, response.status, code);
      case 429:
        return new RateLimitError(message, response.status, code);
      case 422:
        return new ValidationError(message, response.status, code);
      default:
        return new ZapSEAError(message, response.status, code);
    }
  }
}

export class AuthenticationError extends ZapSEAError {
  constructor(message: string, status?: number, code?: string) {
    super(message, status, code);
    this.name = 'AuthenticationError';
  }
}

export class PermissionError extends ZapSEAError {
  constructor(message: string, status?: number, code?: string) {
    super(message, status, code);
    this.name = 'PermissionError';
  }
}

export class NotFoundError extends ZapSEAError {
  constructor(message: string, status?: number, code?: string) {
    super(message, status, code);
    this.name = 'NotFoundError';
  }
}

export class RateLimitError extends ZapSEAError {
  constructor(message: string, status?: number, code?: string) {
    super(message, status, code);
    this.name = 'RateLimitError';
  }
}

export class JobNotFoundError extends NotFoundError {
  constructor(message: string) {
    super(message, 404);
    this.name = 'JobNotFoundError';
  }
}

export class TimeoutError extends ZapSEAError {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

export class ValidationError extends ZapSEAError {
  constructor(message: string, status?: number, code?: string) {
    super(message, status, code);
    this.name = 'ValidationError';
  }
}

export class NetworkError extends ZapSEAError {
  public readonly cause?: Error;

  constructor(message: string, cause?: Error) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}
